'use client';

import { useCallback, useState } from 'react';
import {
  GoogleMap,
  Marker,
  InfoWindow,
  MarkerClusterer,
  useJsApiLoader,
} from '@react-google-maps/api';
import { Cluster } from '@react-google-maps/marker-clusterer';

import { MapCarousel } from './Carousel';
import { Village } from '@/services/types';
import './styles.css';

const center = {
  lat: 37.548134,
  lng: 14.080953,
};

type Props = {
  villages: Village[];
  lang: string;
  customCenter?: {
    lat: number;
    lng: number;
  };
};

export default function MapRoot({ villages, lang, customCenter }: Props) {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [infoWindowPositon, setInfoWindowPosition] =
    useState<google.maps.LatLngLiteral>();
  const [infoWindowData, setInfoWindowData] = useState<Village[]>([]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY || '',
  });

  const onLoad = useCallback(function callback(map: google.maps.Map) {
    /*  const bounds = new window.google.maps.LatLngBounds(customCenter ?? center);
    map.fitBounds(bounds); */

    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback(map: google.maps.Map) {
    setMap(null);
  }, []);

  function showClusterInfoData(clusterer: Cluster) {
    const clusterPosition = clusterer.getCenter()?.toJSON();
    const clusterMarkersLocation = clusterer.getMarkers().map((marker) => {
      return {
        lat: marker.getPosition()?.lat(),
        lng: marker.getPosition()?.lng(),
      };
    });

    const clusterData = villages.filter(({ location }) => {
      return clusterMarkersLocation.some(
        (markerPosition) => markerPosition.lat === location.latitude,
      );
    });

    setInfoWindowPosition(clusterPosition);
    setInfoWindowData(clusterData);
    setIsOpen(true);
  }

  function showMarkerInfoWindow(village: Village) {
    document.getElementById(village.title)?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
    setInfoWindowData(Array(village));

    setInfoWindowPosition({
      lat: village.location?.latitude + 0.26,
      lng: village.location?.longitude,
    });

    setIsOpen(true);
  }

  return (
    isLoaded && (
      <GoogleMap
        mapContainerClassName="relative overflow-hidden w-full h-[calc(100vh-137px)]"
        center={customCenter ?? center}
        zoom={8}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          mapId: '3cf9f101ac9b503c',
          fullscreenControl: false,
          zoomControlOptions: { position: 3.0 },
          tilt: 8,
          zoom: 8,
          minZoom: 8,
          isFractionalZoomEnabled: false,
          keyboardShortcuts: false,
          streetViewControl: false,
          mapTypeControl: false,
        }}>
        <MarkerClusterer
          clusterClass="cluster"
          zoomOnClick={false}
          onClick={(cluster) => showClusterInfoData(cluster)}>
          {(clusterer) => (
            <>
              {isOpen && (
                <InfoWindow
                  position={infoWindowPositon}
                  onCloseClick={() => {
                    setIsOpen(false);
                  }}>
                  <MapCarousel lang={lang} villages={infoWindowData} />
                </InfoWindow>
              )}
              {villages.map((village, i) => (
                <Marker
                  icon={
                    'https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/maps/markers/villa.png'
                  }
                  key={i}
                  position={{
                    lat: village.location?.latitude,
                    lng: village.location?.longitude,
                  }}
                  clusterer={clusterer}
                  onClick={() => showMarkerInfoWindow(village)}
                />
              ))}
            </>
          )}
        </MarkerClusterer>
      </GoogleMap>
    )
  );
}
